import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useGetNewAccountGroupOtherDetails } from "./useGetNewAccountGroupOtherDetails";
import { Controller, useFormContext } from "react-hook-form";
import { Divider } from "antd";
import { FormValues } from ".";
import FormContainer from "components/ui/FormContainer";
import Container from "components/ui/Container";
import TextInput from "components/Inputs/TextInput";
import SingleSelect from "components/Select/SingleSelect";
import { useQueryClient } from "@tanstack/react-query";
import resources from "resources.json";
import InputFieldGroupContainer from "components/ui/InputFieldsGroupContainer";
import TableWithState from "components/ui/Table/TableWithState";
import Icon from "components/Icon";
import Spinner from "components/ui/Spinner";
import { useBuildUrlWithContext } from "hooks/useBuildUrlWithContext";
import usePagination from "hooks/usePagination";

export default function OtherActions() {
  const buildUrlWithContext = useBuildUrlWithContext();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const accountNumber = searchParams.get("accountNumber") ?? "";
  const { control } = useFormContext<FormValues>();

  const [sortedServiceDetails, setSortedServiceDetails] = useState<any[]>([]);

  const { orderBy, sortBy } = usePagination({
    defaultSortBy: "isActionCompleted",
    defaultOrderBy: 'DESC'
  });

  const accountGroupsData = queryClient.getQueryData([
    "changeAccountGroupsInitData",
    accountNumber,
  ]) as any;

  const {
    data: newAccountGroupOtherDetailsData,
    isFetching: isLoadingNewAccountGroupOtherDetails,
  } = useGetNewAccountGroupOtherDetails({
    accountNumber,
  });


  useEffect(() => {
    const serviceDetails = [
      ...(newAccountGroupOtherDetailsData?.serviceDetails ?? []),
    ];

    serviceDetails.sort((a: any, b: any) => {
      const aValue = a?.[sortBy];
      const bValue = b?.[sortBy];

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === "string" && typeof bValue === "string") {
        return orderBy === "ASC"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return orderBy === "ASC"
        ? aValue > bValue
          ? 1
          : -1
        : aValue < bValue
          ? 1
          : -1;
    });

    setSortedServiceDetails(serviceDetails);
  }, [
    newAccountGroupOtherDetailsData?.serviceDetails,
    sortBy,
    orderBy,
  ]);

  const handleLinkClick = (to: string) => {
    if (!to) return;
    const urlWithContext = buildUrlWithContext(to);
    window.open(urlWithContext, "_blank");
  };

  const NEW_ACCOUNT_SETUP_DETAILS_COLUMNS = useMemo(
    () => [
      {
        header: "Setup Detail",
        accessorKey: "key",
        cell: ({ row }: any) => {
          const rowData = row.original;
          return (
            <span>
              {rowData?.key}
              <span style={{ color: "red" }}> *</span>
            </span>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "bool",
        cell: ({ row }: any) => (
          <span>
            {row.original.bool ? (
              <Icon name="check" color="var(--green-color)" size={16} />
            ) : (
              "Pending"
            )}
          </span>
        ),
      },
    ],
    [],
  );

  const SPECIAL_SERVICE_TICKET_COLUMNS = useMemo(
    () => [
      {
        header: "Service Address",
        accessorKey: "serviceAddress"
      },
      {
        header: "Ticket Number",
        accessorKey: "ticketNumber",
        cell: ({ row }: { row: any }) => {
          const { ticketNumber, ticketStatus } = row.original;

          const status = ticketStatus?.toLowerCase();

          const routeMap: Record<string, string> = {
            printed: `/subclient/account-management/special-service-tickets/view/${ticketNumber}?accountNumber=${accountNumber}`,

            closed: `/subclient/account-management/special-service-tickets/view-close-ticket?ticketNumber=${ticketNumber}&accountNumber=${accountNumber}`,

            "new/open": `/subclient/account-management/special-service-tickets/view/${ticketNumber}?accountNumber=${accountNumber}`,
          };

          const to = routeMap[status] ?? "";

          return (
            <span role="link" onClick={() => handleLinkClick(to)} style={{ color: 'var(--primary-color)', cursor: 'pointer' }}>
              {ticketNumber}
            </span>
          );
        }
      },
      {
        header: "Ticket Status",
        accessorKey: "ticketStatus"
      },
      {
        header: "Target Service Date",
        accessorKey: "targetServiceDate"
      },
      {
        header: "Action Status",
        accessorKey: "isActionCompleted",
        cell: ({ row }: any) =>
          row.original.isActionCompleted ? "Done" : "Pending",
      },
      {
        header: "Reason",
        accessorKey: "reason"
      },
    ],
    [],
  );

  const SERVICE_DETAILS_COLUMNS = useMemo(
    () => [
      {
        header: "Service Address",
        accessorKey: "serviceAddress",
        enableSorting: true
      },
      {
        header: "Rate Description",
        accessorKey: "rateDesc",
        enableSorting: true,
        cell: ({ row }: { row: any }) => {
          const {
            serviceDetailId,
            serviceAddressId,
            parentServiceDetailId,
            isActiveService,
            materialName,
            accountName,
            accountNumber,
            rateDesc,
          } = row.original;

          // If materialName exists, show it instead of rateDesc
          // and make it non-clickable
          if (!!materialName) {
            return materialName;
          }

          let url = `/subclient/account-management/accounts/customer-details/add-service-details?activeAddressId=${serviceAddressId}&accountName=${accountName}&accountNumber=${accountNumber}&serviceDetailId=${serviceDetailId}&fromCustomRateSearch=true&isActiveService=${isActiveService}&hasBackButton=false`;

          if (parentServiceDetailId) {
            url = `${url}&parentServiceDetailId=${parentServiceDetailId}`;
          }

          return (
            <span
              role="link"
              onClick={() => handleLinkClick(url)}
              style={{
                color: "var(--primary-color)",
                cursor: "pointer",
              }}
            >
              {rateDesc}
            </span>
          );
        }
      },
      {
        header: "Service ID",
        accessorKey: "serviceDetailId",
        enableSorting: true
      },
      {
        header: "Start Date",
        accessorKey: "startDate",
        enableSorting: true
      },
      {
        header: "Action Status",
        accessorKey: "isActionCompleted",
        enableSorting: true
      },
      {
        header: "Reason",
        accessorKey: "reason",
        enableSorting: true
      },
    ],
    [],
  );

  return (
    <FormContainer>
      <Container className="col col-3">
        <TextInput
          value={accountGroupsData?.oldAccountGroupName}
          disabled
          label={resources.label.CURRENT_ACCOUNT_GROUP_LABEL}
          required
        />
        <Controller
          name="newAccountGroupId"
          control={control}
          render={({ field }) => (
            <SingleSelect
              {...field}
              options={accountGroupsData?.accountGroupList}
              value={
                accountGroupsData?.accountGroupList?.find(
                  (opt: any) => opt.value === field.value,
                ) || null
              }
              label={resources.label.NEW_ACCOUNT_GROUP_LABEL}
              required
              isDisabled
            />
          )}
        />
      </Container>
      <Divider className="line" />
      {isLoadingNewAccountGroupOtherDetails ? (
        <Spinner />
      ) : (
        <>
          <InputFieldGroupContainer
            label={
              resources.label
                .ACCOUNT_SETUP_COMPLETION_DETAILS_FOR_NEW_ACCOUNT_GROUP_LABEL
            }
          >
            <TableWithState
              columns={NEW_ACCOUNT_SETUP_DETAILS_COLUMNS}
              data={
                newAccountGroupOtherDetailsData?.newAccountSetupDetailList ?? []
              }
              isLoading={isLoadingNewAccountGroupOtherDetails}
              noDataMessage={resources.errorMessage.NO_DATA_FOUND}
            />
          </InputFieldGroupContainer>

          <Divider className="line" />

          <SectionTableWrapper
            title="Services"
            note={resources.tooltipMessage.CHANGE_ACCOUNT_GROUP_SERVICE_INFO}
          >
            <TableWithState
              data={sortedServiceDetails}
              columns={SERVICE_DETAILS_COLUMNS}
              noDataMessage={resources.errorMessage.NO_DATA_FOUND}
            />
          </SectionTableWrapper>

          <Divider className="line" />

          <SectionTableWrapper
            title="Active Special Service Ticket"
            note={
              resources.tooltipMessage
                .CHANGE_ACCOUNT_GROUP_SPECIAL_SERVICE_TICKET_INFO
            }
          >
            <TableWithState
              data={newAccountGroupOtherDetailsData?.specialServiceTickets}
              columns={SPECIAL_SERVICE_TICKET_COLUMNS}
              noDataMessage={resources.errorMessage.NO_DATA_FOUND}
            />
          </SectionTableWrapper>
        </>
      )
      }
    </FormContainer>
  );
}


interface SectionTableWrapperProps {
  title: string;
  note: string;
  children: React.ReactNode;
}

function SectionTableWrapper({
  title,
  note,
  children,
}: SectionTableWrapperProps) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "10px",
          gap: "10px",
        }}
      >
        <span
          style={{
            fontWeight: "var(--bold)",
            color: "var(--grey-text)",
            fontSize: "var(--font-md)",
          }}
        >
          {title}
        </span>

        <span
          style={{
            marginLeft: "auto",
            fontSize: "var(--font-sm)",
            fontWeight: "var(--semibold)",
          }}
        >
          {note}
        </span>
      </div>

      {children}
    </div>
  );
}
